/**
 * stage 별 커스텀 메타 정보를 보여준다.
 *  - Extract: 진입 경로 + 에셋 개수
 *  - Upload: 업로드된 에셋 목록 (경로 → S3)
 *  - Scan: AnchorScanReport (에러/요약)
 *  - Adapt / Normalize / Substitute / Autofix / Inject: stage log 의 info
 *  - LLM: 토큰 사용량
 *  - Validate: errors + warnings
 */

import type { StageName, StepwiseState, PipelineStageLog } from './stages'

interface Props {
  stage: StageName
  snapshot?: StepwiseState
}

const cardStyle: React.CSSProperties = {
  padding: 12,
  background: '#fafafa',
  border: '1px solid #e5e5e5',
  borderRadius: 6,
  fontSize: 13,
  overflow: 'auto',
}

function findLog(snapshot: StepwiseState | undefined, stage: StageName): PipelineStageLog | undefined {
  return snapshot?.stages.find((s) => s.stage === stage)
}

export function StageMetaPanel({ stage, snapshot }: Props) {
  if (!snapshot) {
    return <div style={{ padding: 16, color: '#888' }}>아직 실행되지 않은 단계입니다.</div>
  }
  const log = findLog(snapshot, stage)

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: 16, height: '100%', overflow: 'auto' }}>
      {/* 공통 stage log */}
      {log && (
        <div style={cardStyle}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>
            Stage log: {log.stage} {log.ok ? '✅' : '❌'}
          </div>
          {log.info && (
            <pre style={{ margin: 0, fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {JSON.stringify(log.info, null, 2)}
            </pre>
          )}
        </div>
      )}

      {/* stage 별 특화 정보 */}
      {stage === 'upload' && snapshot.artifacts && (
        <div style={cardStyle}>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>
            업로드된 에셋 ({snapshot.artifacts.uploadedAssets.length})
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 280, overflow: 'auto' }}>
            {snapshot.artifacts.uploadedAssets.map((a) => (
              <div key={a.s3Key} style={{ fontSize: 12, fontFamily: 'monospace' }}>
                <span style={{ color: '#888' }}>[{a.category}]</span> {a.originalPath} →{' '}
                <a href={a.s3Url} target="_blank" rel="noreferrer" style={{ color: '#06c' }}>
                  {a.s3Key}
                </a>
              </div>
            ))}
          </div>
        </div>
      )}

      {stage === 'scan' && snapshot.anchors && (
        <div style={cardStyle}>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>앵커 스캔 결과</div>
          {snapshot.anchors.errors.length > 0 ? (
            <ul style={{ margin: 0, paddingLeft: 18, color: '#900' }}>
              {snapshot.anchors.errors.map((e, i) => (
                <li key={i}>
                  <code>{e.code}</code> — {e.message}
                  {e.hint && <div style={{ color: '#666', fontSize: 12 }}>{e.hint}</div>}
                </li>
              ))}
            </ul>
          ) : (
            <div style={{ color: '#060' }}>에러 없음 — 필수 앵커 모두 존재</div>
          )}
        </div>
      )}

      {stage === 'llm' && snapshot.llmUsage && (
        <div style={cardStyle}>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>LLM 사용량</div>
          <div>model: <code>{snapshot.llmUsage.model}</code></div>
          <div>input: {snapshot.llmUsage.inputTokens} · output: {snapshot.llmUsage.outputTokens}</div>
          <div>cache read: {snapshot.llmUsage.cacheReadTokens} · cache create: {snapshot.llmUsage.cacheCreationTokens}</div>
        </div>
      )}

      {stage === 'autofix' && (
        <div style={cardStyle}>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>Autofix 적용 (누적)</div>
          {snapshot.autofix.applied.length === 0 ? (
            <div style={{ color: '#888' }}>적용된 항목 없음</div>
          ) : (
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {snapshot.autofix.applied.map((s, i) => (
                <li key={i}><code style={{ fontSize: 12 }}>{s}</code></li>
              ))}
            </ul>
          )}
          {snapshot.autofix.skipped.length > 0 && (
            <div style={{ marginTop: 6, color: '#888' }}>
              skipped: {snapshot.autofix.skipped.length}
            </div>
          )}
        </div>
      )}

      {stage === 'validate' && (
        <>
          <div style={cardStyle}>
            <div style={{ fontWeight: 600, marginBottom: 6, color: snapshot.validation.errors.length > 0 ? '#900' : '#060' }}>
              에러 ({snapshot.validation.errors.length})
            </div>
            {snapshot.validation.errors.length === 0 ? (
              <div style={{ color: '#060' }}>에러 없음</div>
            ) : (
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {snapshot.validation.errors.map((e, i) => (
                  <li key={i}><code>{e.code}</code> — {e.message}</li>
                ))}
              </ul>
            )}
          </div>
          <div style={cardStyle}>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>
              경고 ({snapshot.validation.warnings.length})
            </div>
            {snapshot.validation.warnings.length === 0 ? (
              <div style={{ color: '#888' }}>경고 없음</div>
            ) : (
              <ul style={{ margin: 0, paddingLeft: 18, color: '#a60' }}>
                {snapshot.validation.warnings.map((w, i) => (
                  <li key={i}><code>{w.code}</code> — {w.message}</li>
                ))}
              </ul>
            )}
          </div>
        </>
      )}

      {stage === 'inject' && snapshot.injected && (
        <div style={cardStyle}>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>주입된 컨트롤러 ({snapshot.injected.length})</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {snapshot.injected.map((c) => (
              <span key={c} style={{ padding: '2px 8px', background: '#e6f4ff', border: '1px solid #91d5ff', borderRadius: 4, fontSize: 12 }}>
                {c}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* stage 별 에러가 누적되어 있으면 보여줌 */}
      {snapshot.errors.filter((e) => e.stage === stage).length > 0 && (
        <div style={{ ...cardStyle, background: '#fff5f5', border: '1px solid #ffa39e' }}>
          <div style={{ fontWeight: 600, marginBottom: 6, color: '#900' }}>이 단계의 에러</div>
          <ul style={{ margin: 0, paddingLeft: 18, color: '#900' }}>
            {snapshot.errors
              .filter((e) => e.stage === stage)
              .map((e, i) => (
                <li key={i}>
                  {e.code && <code>[{e.code}] </code>}
                  {e.message}
                </li>
              ))}
          </ul>
        </div>
      )}
    </div>
  )
}
